/**
 * NavRailBadge — a state the nav rail reports, shaped as one of the rail's own rows so it lines
 * up with the buttons around it: icon-only on a narrow rail, icon and label once it is wide
 * enough, and a finger-sized row on the phone. Given `onClick` it is a button; without one it is
 * a status, since a state with nowhere to go should not look pressable. Its styles are those of
 * NavRailButton, in navRailButton.css.
 */

import './ui/navRailButton.css';

import type { ComponentType, ReactElement } from 'react';
import React from 'react';

import type { StatusTone } from '@/types/status';

import { Tooltip } from './ui/Tooltip';
import { TooltipContent } from './ui/TooltipContent';
import { TooltipTrigger } from './ui/TooltipTrigger';

interface Props {
    icon: ComponentType<{ className?: string }>;
    label: string;
    /** A longer account of the state, for hover and screen readers; `label` stands in for it. */
    detail?: string;
    tone: StatusTone;
    /** The rail is wide enough to show labels. */
    isLabelled: boolean;
    /** A row sized for a finger rather than a pointer. */
    isRoomy?: boolean;
    onClick?: () => void;
}

export const NavRailBadge = ({
    icon: Icon, label, detail, tone, isLabelled, isRoomy = false, onClick,
}: Props): ReactElement => {
    const name = detail ?? label;
    const shape = {
        className: 'occ-nav-button',
        'data-tone': tone,
        'data-expanded': isLabelled || undefined,
        'data-roomy': isRoomy || undefined,
    };
    const content = (
        <>
            <Icon className="occ-nav-button-icon" />
            {isLabelled && <span className="occ-nav-button-label">{label}</span>}
        </>
    );
    // A live region announces its content rather than its tooltip, so the name is set outright.
    const badge = onClick ? (
        <button
            type="button"
            onClick={onClick}
            aria-label={name}
            {...shape}
        >
            {content}
        </button>
    ) : (
        <div
            role="status"
            aria-label={name}
            {...shape}
        >
            {content}
        </div>
    );
    // With the label showing and nothing more to say, a tooltip would only repeat it.
    if (isLabelled && detail === undefined) {
        return badge;
    }
    return (
        <Tooltip>
            <TooltipTrigger asChild>{badge}</TooltipTrigger>
            <TooltipContent side="right">{name}</TooltipContent>
        </Tooltip>
    );
};
